/**
 * The calculator's state: which engine, which Qwen region, which preset (or
 * Custom), and the settings the price is computed from.
 *
 * A REDUCER, so every rule below is a pure function the tests drive directly
 * rather than behaviour buried in a component's event handlers:
 *
 * - Picking a preset sets its settings. Changing any setting by hand selects
 *   Custom and leaves everything else as it is.
 * - Two people cannot speak for more than the whole hour between them.
 *   Dragging one share past it pushes the other down to what is left; a side
 *   switched on takes what the other leaves. A value the visitor set is never
 *   moved except by a drag.
 * - Switching engine keeps the preset if the new engine lists it, else selects
 *   its preset with identical settings (face to face becomes two-way on Qwen),
 *   else Custom. The settings never change on a switch, except that Soniox has
 *   no screen context, so picking it clears that switch.
 * - The screen-context switch is a hand change like any other: it selects
 *   Custom (no preset turns it on), and a preset picked afterwards leaves it off.
 * - The region is its own choice: only a region pick changes it. Presets,
 *   hand changes and an engine switch all leave it alone, so Beijing is still
 *   picked after a visit to Soniox (which has no region of its own).
 * - A face-to-face preset puts both directions on one shared stream, and the
 *   shared stream stays through hand changes (two people at one microphone are
 *   still at one microphone when one of them talks more). Only another preset,
 *   or an engine without a shared stream, takes it away.
 */
import type { CalcSettings, Engine, Output, QwenRegion, Side } from './cost-model';
import {
  DEFAULT_SCENARIO,
  ENGINE_SCENARIOS,
  matchingScenario,
  scenarioById,
  type ScenarioChoice,
} from './calculator-scenarios';

export interface CalcState {
  engine: Engine;
  /** Which site Qwen is priced at; Soniox has none and ignores it. */
  region: QwenRegion;
  scenario: ScenarioChoice;
  settings: CalcSettings;
  /** Both directions over one stream: one connection (face to face). */
  sharedStream: boolean;
}

export type CalcAction =
  | { type: 'pickEngine'; engine: Engine }
  | { type: 'pickRegion'; region: QwenRegion }
  | { type: 'pickScenario'; scenario: ScenarioChoice }
  | { type: 'setOutput'; side: Side; output: Output }
  | { type: 'setShare'; side: Side; percent: number }
  | { type: 'setScreenContext'; on: boolean };

const SHARE_STEP = 5;

const shareKey = (side: Side): 'themShare' | 'meShare' => (side === 'them' ? 'themShare' : 'meShare');
const otherSide = (side: Side): Side => (side === 'them' ? 'me' : 'them');
const snap = (percent: number) =>
  Math.min(100, Math.max(0, Math.round(percent / SHARE_STEP) * SHARE_STEP));

/** Whether the engine can run both directions over one stream: it lists a shared-stream preset. */
const sharesStream = (engine: Engine) => ENGINE_SCENARIOS[engine].some((id) => scenarioById(id).sharedStream);

export function initialCalcState(engine: Engine, region: QwenRegion): CalcState {
  const preset = scenarioById(DEFAULT_SCENARIO);
  return { engine, region, scenario: DEFAULT_SCENARIO, settings: { ...preset.settings }, sharedStream: preset.sharedStream };
}

export function calcReducer(state: CalcState, action: CalcAction): CalcState {
  switch (action.type) {
    case 'pickEngine': {
      const listed = ENGINE_SCENARIOS[action.engine];
      const scenario =
        state.scenario === 'custom' || listed.includes(state.scenario)
          ? state.scenario
          : (matchingScenario(action.engine, state.settings) ?? 'custom');
      // Soniox cannot see a screen: the flag goes with the engine, and the key with it.
      const { screenContext: _dropped, ...kept } = state.settings;
      const settings = action.engine === 'soniox' ? kept : state.settings;
      return { ...state, engine: action.engine, scenario, settings, sharedStream: state.sharedStream && sharesStream(action.engine) };
    }
    case 'pickRegion':
      return { ...state, region: action.region };
    case 'pickScenario': {
      if (action.scenario === 'custom') return { ...state, scenario: 'custom' };
      // The card only offers the engine's own presets; anything else is ignored
      // rather than leaving, say, a face-to-face preset selected on Qwen.
      if (!ENGINE_SCENARIOS[state.engine].includes(action.scenario)) return state;
      const preset = scenarioById(action.scenario);
      return { ...state, scenario: action.scenario, settings: { ...preset.settings }, sharedStream: preset.sharedStream };
    }
    case 'setOutput': {
      const { side, output } = action;
      const other = otherSide(side);
      const settings = { ...state.settings, [side]: output };
      const switchedOn = state.settings[side] === 'off' && output !== 'off';
      if (switchedOn && settings[other] !== 'off') {
        const room = 100 - settings[shareKey(other)];
        if (settings[shareKey(side)] > room) settings[shareKey(side)] = room;
      }
      return { ...state, scenario: 'custom', settings };
    }
    case 'setShare': {
      if (!Number.isFinite(action.percent)) return state;
      const { side } = action;
      const other = otherSide(side);
      const value = snap(action.percent);
      const settings = { ...state.settings, [shareKey(side)]: value };
      if (settings[side] !== 'off' && settings[other] !== 'off' && value + settings[shareKey(other)] > 100) {
        settings[shareKey(other)] = 100 - value;
      }
      return { ...state, scenario: 'custom', settings };
    }
    case 'setScreenContext':
      return { ...state, scenario: 'custom', settings: { ...state.settings, screenContext: action.on } };
  }
}

/** Whether the price counts one shared connection for both directions (face to face). */
export function isSharedStream(state: CalcState): boolean {
  return state.sharedStream;
}
